import cn, { Argument as ClassName } from 'classnames'
import React, { useEffect, useState } from 'react'

import { notifyError } from '../../common/features/notificationsManager/Notify'
import {
    ChargePoint,
    useChargePointUpdateMutation,
    useGreenChargingChargePointStatusQuery,
} from '../../common/redux/api/web-api'
import { CapiAuraConnectorControl } from '../capi-aura-connector-control'
import { isUserHasAccessToConnector, isUserHasAccessToAnyConnector } from '../is-user-has-access-to-connector'
import { RemoteCapiDimmerControl } from '../remote-capi-dimmer-control'
import { RemoteControlWidget } from '../remote-control-widget'
import useText from '../../common/hooks/useText'

export type ChargePointCapiAuraControlWidgetProps = {
    chargePoint: ChargePoint
    userId?: string
    isReadOnly?: boolean
    className?: ClassName
    style?: React.CSSProperties
}

export function ChargePointCapiAuraControlWidget({
    chargePoint: initialChargePoint,
    userId,
    isReadOnly = false,
    className,
    style,
}: ChargePointCapiAuraControlWidgetProps): React.ReactElement {
    useEffect(() => setChargePoint(initialChargePoint), [initialChargePoint])

    const text = useText()

    const isOnline = initialChargePoint.chargePointStatus !== 'Offline'
    const { data: chargePointStatus } = useGreenChargingChargePointStatusQuery({ chargePointId: initialChargePoint.id })

    const [chargePoint, setChargePoint] = useState(initialChargePoint)

    const [updateChargePoint] = useChargePointUpdateMutation()

    const applyChargePoint = async (val: Partial<ChargePoint>) => {
        try {
            const tempChargePoint = {
                ...chargePoint,
                ...val,
            }
            await updateChargePoint({ chargePoint: { ...chargePoint, ...val } }).unwrap()
            setChargePoint(tempChargePoint)
        } catch (_) {
            notifyError(text('msgChargerFailedToUpdate'))
        }
    }

    return (
        <RemoteControlWidget
            greenChargingStatus={chargePointStatus?.greenChargingStatus}
            isReadOnly={isReadOnly}
            chargePoint={chargePoint}
            title={'lblAuraControlWidget'}
            className={cn(className)}
            style={style}
        >
            {chargePoint?.connectors && (
                <>
                    {chargePoint?.connectors?.map(
                        (connector, index) =>
                            isUserHasAccessToConnector(connector, chargePoint.ownerId, userId) && (
                                <CapiAuraConnectorControl
                                    key={`capi-control-${index}`}
                                    connector={connector}
                                    applyChargePoint={applyChargePoint}
                                    isReadOnly={isReadOnly}
                                    isOnline={isOnline}
                                    chargePoint={chargePoint}
                                    greenChargingStatus={chargePointStatus?.greenChargingStatus}
                                />
                            )
                    )}
                    {isUserHasAccessToAnyConnector(chargePoint.connectors, chargePoint.ownerId, userId) && (
                        <RemoteCapiDimmerControl
                            chargePoint={chargePoint}
                            applyChargePoint={applyChargePoint}
                            isReadOnly={isReadOnly}
                        />
                    )}
                </>
            )}
        </RemoteControlWidget>
    )
}
